interface IAndroidWebViewApi {
    /** Trigger WebView Event. This will also hide the loading animation in WebView */
    onFinishLoaded(): void

    /** Trigger WebView to update stored cookies */
    updateCookies(): void

    /** Trigger when location.pathname changed */
    onPathnameChanged(pathname: string): void

    /** Trigger toggle fullscreen in WebView App */
    toggleFullscreen(): void

    /** Trigger event onPaymentWithPaynt with received transaction ID */
    onPaymentWithPaynt(trackingUrl: string): void

    // ==================== Printer Methods ====================

    /** Get printer settings from Android app */
    getPrinterSettings(): string

    /** Print image from base64 data */
    printImage(base64Data: string, width: number, callbackId: string): void

    /** Cut paper */
    cutPaper(fullCut: boolean, callbackId: string): void
}

// Callback registry for async printer operations
declare global {
    interface Window {
        OwAndroidCallbacks?: {
            [key: string]: (result: PrintResult) => void
        }
    }
}

// Initialize callback registry
if (typeof window !== 'undefined' && !window.OwAndroidCallbacks) {
    window.OwAndroidCallbacks = {}
}

export interface PrintResult {
    success: boolean
    message?: string
    code?: number
}

export type EmbeddedPrinterMode = 'ONLY_EMBEDDED' | 'BOTH'

export interface PrinterSettings {
    /** Whether embedded printer is enabled */
    embeddedPrinterEnabled: boolean
    /** Print mode: ONLY_EMBEDDED = only use embedded printer, BOTH = use both embedded and HW Agent */
    embeddedPrinterMode: EmbeddedPrinterMode
    /** Detected or configured printer brand (PAX, SUNMI, etc.) */
    printerBrand: string
    /** PAX-specific gray level setting (1-4) */
    paxGrayLevel: number
    /** PAX-specific threshold setting (0-255) */
    paxThreshold: number
}

// Helper to create callback for async operations
function createPrinterCallback<T>(timeoutMs: number = 30000): {
    callbackId: string
    promise: Promise<T>
} {
    const callbackId = `cb_${Date.now()}_${Math.random()
        .toString(36)
        .substr(2, 9)}`

    const promise = new Promise<T>((resolve, reject) => {
        const timeout = setTimeout(() => {
            if (window.OwAndroidCallbacks) {
                delete window.OwAndroidCallbacks[callbackId]
            }
            reject(new Error('Print operation timed out'))
        }, timeoutMs)

        if (window.OwAndroidCallbacks) {
            window.OwAndroidCallbacks[callbackId] = (result: any) => {
                clearTimeout(timeout)
                if (window.OwAndroidCallbacks) {
                    delete window.OwAndroidCallbacks[callbackId]
                }
                if (result.success === false) {
                    reject(
                        new Error(
                            result.message || `Error code: ${result.code}`,
                        ),
                    )
                } else {
                    resolve(result as T)
                }
            }
        }
    })

    return { callbackId, promise }
}

const owAndroidObject = (window as any).OwAndroid as
    | IAndroidWebViewApi
    | undefined

const androidWebViewApi = {
    updateCookies() {
        owAndroidObject?.updateCookies()
    },
    onFinishLoaded() {
        owAndroidObject?.onFinishLoaded()
    },
    onPathnameChanged(pathname: string) {
        owAndroidObject?.onPathnameChanged(pathname)
    },
    toggleFullscreen() {
        owAndroidObject?.toggleFullscreen()
    },
    isRunInAndroidWebView() {
        return Boolean(owAndroidObject)
    },
    onPaymentWithPaynt(trackingUrl: string) {
        owAndroidObject?.onPaymentWithPaynt(trackingUrl)
    },

    // ==================== Printer Methods ====================

    /** Check if WebView printer support is available */
    hasPrinterSupport(): boolean {
        return Boolean(owAndroidObject?.printImage)
    },

    /**
     * Get printer settings from Android WebView app
     * Returns settings including whether embedded printer is enabled and print mode
     */
    getPrinterSettings(): PrinterSettings | null {
        if (!owAndroidObject?.getPrinterSettings) {
            return null
        }
        try {
            const jsonStr = owAndroidObject.getPrinterSettings()
            return JSON.parse(jsonStr) as PrinterSettings
        } catch (e) {
            console.error('Failed to parse printer settings:', e)
            return null
        }
    },

    /**
     * Check if embedded printer is enabled in Android WebView settings
     * Returns true if running in WebView AND embedded printer is enabled
     */
    isEmbeddedPrinterEnabled(): boolean {
        const settings = this.getPrinterSettings()
        return settings?.embeddedPrinterEnabled ?? false
    },

    /**
     * Check if we should ONLY use embedded printer (skip HW Agent)
     * Returns true if embedded printer mode is ONLY_EMBEDDED
     */
    shouldOnlyUseEmbeddedPrinter(): boolean {
        const settings = this.getPrinterSettings()
        return (
            settings?.embeddedPrinterEnabled === true &&
            settings?.embeddedPrinterMode === 'ONLY_EMBEDDED'
        )
    },

    /** Print image from base64 data */
    async printImage(
        base64Data: string,
        width: number = 384,
    ): Promise<PrintResult> {
        if (!owAndroidObject?.printImage) {
            return { success: false, message: 'Printer not available' }
        }
        const { callbackId, promise } = createPrinterCallback<PrintResult>()
        owAndroidObject.printImage(base64Data, width, callbackId)
        return promise
    },

    /** Cut paper */
    async cutPaper(fullCut: boolean = true): Promise<PrintResult> {
        if (!owAndroidObject?.cutPaper) {
            return { success: false, message: 'Printer not available' }
        }
        const { callbackId, promise } = createPrinterCallback<PrintResult>()
        owAndroidObject.cutPaper(fullCut, callbackId)
        return promise
    },
}

export default androidWebViewApi
